import {
  Paper,
  Modal,
  Box,
  Typography,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useState, useEffect } from "react";
import {
  useAllChallengeNames,
  useAllFundNames,
  useProperties,
} from "../../lib/hooks";
import styles from "./../../styles/charts/chartStyle.module.css";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import {
  getChartBottomIcons,
  getChartInfoIcons,
} from "./twitter/TwitterWordCloud";
import { useNavigate } from "react-router-dom";
import BubbleChart from "./protoCharts/BubbleChart";
import { underscoreToCap } from "../../lib/util";
import { PropertiesFormType } from "../../lib/analyticsAPI";
import { desc } from "../../lib/chartDescription";

const names = [
  "amount_requested",
  "result",
  "yes",
  "no",
  "rating",
  "challenge_amount",
  "number_of_proposals",
  "proportion_requested",
  "funded_percentage",
];
const z_names = [
  "amount_requested",
  "yes",
  "no",
  "rating",
  "challenge_amount",
  "number_of_proposals",
  "proportion_requested",
  "funded_percentage",
];

function to_String(arr: any[]) {
  let str: string = arr.join(",");
  return "[" + str + "]";
}

const PropertiesBubblePlot = () => {
  const [selectedX, setSelectedX] = useState<string>("rating");
  const [selectedY, setSelectedY] = useState<string>("result");
  const [selectedZ, setSelectedZ] = useState<string>("amount_requested");
  const [grouping, setGrouping] = useState<string>("Binary");
  const [fund_id, setFund_id] = useState<any[]>([]);
  const [challenge_id, setChallenge_id] = useState<any[]>([]);

  const [formData, setFormData] = useState<PropertiesFormType>({
    property_names: to_String(["rating", "result", "amount_requested"]),
    status_grouping: "Binary",
    include_decision_boundary: "False",
    include_proposal_id: "True",
    challenge_id: "All",
    fund_id: "All",
  });

  const {
    allChallengeNames,
    loading: allChallengeNameLoading,
    error: allChallengeNameError,
  } = useAllChallengeNames();

  let {
    loading: fundNameLoading,
    allFundNames,
    error: fundNamError,
  } = useAllFundNames();

  let challenge_dropDownOption =
    !fundNameLoading &&
    !allChallengeNameLoading &&
    allChallengeNameError === null &&
    fundNamError === null
      ? fund_id.length > 0
        ? allChallengeNames
            .filter((item) =>
              fund_id.map((item) => item.id).includes(item.fund_id)
            )
            .map((item) => {
              return {
                label: `${item.fund_name}:${item.challenge_name}`,
                id: item.challenge_id,
              };
            })
        : allChallengeNames.map((item) => {
            return {
              label: `${item.fund_name}:${item.challenge_name}`,
              id: item.challenge_id,
            };
          })
      : [];

  useEffect(() => {
    if (challenge_id.length > 0) {
      let newArr = challenge_id.filter((challenge) =>
        fund_id
          .map((item) => item.id)
          .includes(
            allChallengeNames.filter(
              (allChallenge) => allChallenge.challenge_id === challenge.id
            )[0]?.fund_id
          )
      );
      setChallenge_id(newArr);
    }
    //eslint-disable-next-line
  }, [fund_id.length]);

  const { propertiesData, loading } = useProperties(formData);

  let legend: string[] = [];
  let data: any[] = [];

  if (!loading) {
    propertiesData.scatter_plot_data.forEach((item: any) => {
      legend.push(item.proposal_status);
      let sublist: any[] = [];
      item.items.forEach((dp: any) => {
        sublist.push({
          x: Math.round(dp.column1 * 100) / 100,
          y: dp.column2,
          z: dp.column3,
          p_id: dp.proposal_id,
        });
      });
      data.push(sublist);
    });
  }

  function genTitle() {
    return "Proposal Bubble Plot";
  }

  let navigate = useNavigate();
  const routeChange = (path: string) => {
    navigate(path);
  };

  function jumpToProposal(proposalID: number) {
    routeChange(`/proposal/${proposalID}`);
  }

  const [open, setOpen] = useState(false);
  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    setFormData((prevState: any) => ({
      ...prevState,
      fund_id: fund_id.length === 0 ? "All" : fund_id.map((item) => item.id),
      challenge_id:
        challenge_id.length === 0 ? "All" : challenge_id.map((item) => item.id),
      property_names: to_String([selectedX, selectedY, selectedZ]),
      status_grouping: grouping,
      include_decision_boundary: "False",
      include_proposal_id: "True",
    }));
  }, [selectedX, selectedY, selectedZ, grouping, challenge_id, fund_id]);

  const content = (showMaxButton: boolean) => {
    return (
      <Paper elevation={3} className={styles.chartContainer}>
        <div className="horizontalCenter">
          <Typography className={styles.chartHeader}>{genTitle()}</Typography>
        </div>

        <div className="horizontalCenter">
          <div className={styles.flexibleControlRow}>
            <FormControl
              className={styles.flexibleformControlFullWidth}
              size="small"
            >
              <Autocomplete
                multiple={true}
                options={
                  !fundNameLoading && fundNamError === null
                    ? allFundNames.map((item) => {
                        return {
                          label: item.fund_name,
                          id: item.fund_id,
                        };
                      })
                    : []
                }
                size={"small"}
                onChange={(event: any, newValue: any[] | null) => {
                  setFund_id(newValue as any[]);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={fund_id}
                renderInput={(params) => (
                  <TextField {...params} label="All Funds" />
                )}
              />
            </FormControl>

            <FormControl
              className={styles.flexibleformControlFullWidth}
              size="small"
            >
              <Autocomplete
                multiple={true}
                id="combo-box-demo"
                options={challenge_dropDownOption}
                size={"small"}
                onChange={(event: any, newValue: any[] | null) => {
                  setChallenge_id(newValue as any[]);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                // disableClearable
                value={challenge_id}
                renderInput={(params) => (
                  <TextField {...params} label="All Challenges" />
                )}
              />
            </FormControl>
          </div>
        </div>

        <div className={styles.flexibleControlRow}>
          <FormControl className={styles.flexibleformControl} size="small">
            <InputLabel className={styles.flexSelectFont}>
              Grouping Status
            </InputLabel>
            <Select
              className={styles.flexSelectFont}
              value={grouping}
              label="Grouping Status"
              onChange={(event) => setGrouping(event.target.value)}
              defaultValue="Binary"
            >
              {["Binary", "Ternary"].map((c: any, i: any) => (
                <MenuItem value={c} key={i}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl className={styles.flexibleformControl} size="small">
            <InputLabel className={styles.flexSelectFont}>
              Size Property
            </InputLabel>
            <Select
              className={styles.flexSelectFont}
              value={selectedZ}
              label={selectedZ}
              onChange={(event) => setSelectedZ(event.target.value)}
              defaultValue="Amount"
            >
              {z_names.map((c: any, i: any) => (
                <MenuItem value={c} key={i}>
                  {underscoreToCap(c)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <div className={styles.flexibleControlRow}>
          <FormControl className={styles.flexibleformControl} size="small">
            <InputLabel className={styles.flexSelectFont}>
              X-axis Property
            </InputLabel>
            <Select
              className={styles.flexSelectFont}
              value={selectedX}
              label={"X-axis property"}
              onChange={(event) =>
                event.target.value != selectedY
                  ? setSelectedX(event.target.value)
                  : alert("X and Y property cannot the same")
              }
              defaultValue="Rating"
            >
              {names.map((c: any, i: any) => (
                <MenuItem value={c} key={i}>
                  {underscoreToCap(c)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl className={styles.flexibleformControl} size="small">
            <InputLabel className={styles.flexSelectFont}>
              Y-axis Property
            </InputLabel>
            <Select
              className={styles.flexSelectFont}
              value={selectedY}
              label={" Y-axis property"}
              onChange={(event) =>
                event.target.value != selectedX
                  ? setSelectedY(event.target.value)
                  : alert("X and Y property cannot the same")
              }
              defaultValue="ResultVotes"
            >
              {names.map((c: any, i: any) => (
                <MenuItem value={c} key={i}>
                  {underscoreToCap(c)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <BubbleChart
          xLabel={underscoreToCap(selectedX)}
          yLabel={underscoreToCap(selectedY)}
          zLabel={underscoreToCap(selectedZ)}
          chartData={data}
          legend={legend}
          jumpToProposal={(proposalID: number) => jumpToProposal(proposalID)}
        />

        {getChartBottomIcons(showMaxButton, setOpen)}
        {getChartInfoIcons(desc["proposal"]["bubble"])}
      </Paper>
    );
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className={styles.fullScreenChart}>{content(false)}</Box>
      </Modal>
      {content(true)}
    </>
  );
};

export default PropertiesBubblePlot;
